import React, { useEffect, useMemo, useRef, useState } from "react";
import Loading from "components/Loading";
import axios from "utils/axios";
import { GET_VENDOR_TAXONOMY_URL } from "config/services";
import VendorTaxonomySearch from "./VendorTaxonomySearch";
import VendorMakeSelections from "./VendorMakeSelections";
import { makeSelectionMap, materialMakeSelections } from "services/vendorMakeService";

const getId = (value) => value?.toString?.() || "";
const buildKey = (...parts) => parts.map(getId).join("|");

const AddCategoryPopUp = ({
  open,
  onClose,
  onApply,
  loading,
  selectedVendors = [],
  showError,
  title = "Add Category",
  applyButtonLabel = "Add Category",
  footerNote = "Existing categories will be skipped automatically, so duplicates will not be added.",
  variant = "modal",
}) => {
  const [taxonomyAll, setTaxonomyAll] = useState({});
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [taxonomyLoaded, setTaxonomyLoaded] = useState(false);
  const [expandedVendorTypes, setExpandedVendorTypes] = useState({});
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [selectedDisciplines, setSelectedDisciplines] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [selectedSubCategories, setSelectedSubCategories] = useState(new Set());
  const [makeIdsBySubcategory, setMakeIdsBySubcategory] = useState({});
  const [highlightedCategoryRefKey, setHighlightedCategoryRefKey] = useState("");
  const [drawerMounted, setDrawerMounted] = useState(open);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const categoryRefs = useRef({});
  const categoryHighlightTimerRef = useRef(null);
  const isDrawer = variant === "drawer";

  const isSingleVendor = selectedVendors.length === 1;
  const singleVendorCategories = useMemo(
    () => isSingleVendor ? selectedVendors[0]?.vendorData?.vendorCategories || [] : [],
    [isSingleVendor, selectedVendors]
  );

  useEffect(() => {
    if (open) return;

    setCategorySearchTerm("");
    setHighlightedCategoryRefKey("");
    categoryRefs.current = {};
    window.clearTimeout(categoryHighlightTimerRef.current);
  }, [open]);

  useEffect(() => {
    if (!isDrawer) return undefined;

    let animationFrame;
    let unmountTimer;

    if (open) {
      setDrawerMounted(true);
      animationFrame = window.requestAnimationFrame(() => {
        setDrawerVisible(true);
      });
    } else {
      setDrawerVisible(false);
      unmountTimer = window.setTimeout(() => {
        setDrawerMounted(false);
      }, 300);
    }

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(unmountTimer);
    };
  }, [isDrawer, open]);

  useEffect(() => {
    if (!open) return;

    const fetchTaxonomy = async () => {
      try {
        setTaxonomyLoaded(false);
        setTaxonomyLoading(true);
        const res = await axios.get(GET_VENDOR_TAXONOMY_URL);
        const taxonomyData = res?.data?.data || {};
        setTaxonomyAll(taxonomyData);
        setExpandedVendorTypes(
          Object.keys(taxonomyData).reduce((acc, vendorType) => {
            acc[vendorType] = false;
            return acc;
          }, {})
        );
      } catch (error) {
        console.error("Failed loading vendor taxonomy", error);
        setTaxonomyAll({});
      } finally {
        setTaxonomyLoading(false);
        setTaxonomyLoaded(true);
      }
    };

    fetchTaxonomy();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const disciplineKeys = new Set();
    const categoryKeys = new Set();
    const subCategoryKeys = new Set();

    if (isSingleVendor) {
      singleVendorCategories.forEach((typeEntry) => {
        const vendorType = typeEntry?.vendorType;
        typeEntry?.disciplines?.forEach((discipline) => {
          const disciplineId = getId(discipline?.disciplineId);
          if (!vendorType || !disciplineId) return;

          disciplineKeys.add(buildKey(vendorType, disciplineId));

          discipline?.categories?.forEach((category) => {
            const categoryId = getId(category?.categoryId);
            if (!categoryId) return;

            categoryKeys.add(buildKey(vendorType, disciplineId, categoryId));

            category?.subCategories?.forEach((subCategory) => {
              const subCategoryId = getId(subCategory?.subCategoryId);
              if (subCategoryId) {
                subCategoryKeys.add(
                  buildKey(vendorType, disciplineId, categoryId, subCategoryId)
                );
              }
            });
          });
        });
      });
    }

    setSelectedDisciplines(disciplineKeys);
    setSelectedCategories(categoryKeys);
    setSelectedSubCategories(subCategoryKeys);
    setMakeIdsBySubcategory(makeSelectionMap(singleVendorCategories));
  }, [open, isSingleVendor, singleVendorCategories]);

  useEffect(() => {
    return () => {
      window.clearTimeout(categoryHighlightTimerRef.current);
    };
  }, []);

  const hasSelection =
    selectedDisciplines.size > 0 ||
    selectedCategories.size > 0 ||
    selectedSubCategories.size > 0;

  const selectedVendorNames = useMemo(
    () => selectedVendors
      .map((vendor) => vendor?.Company || vendor?.vendorName || vendor?.businessName)
      .map((name) => Array.isArray(name) ? name.join(", ") : name)
      .filter(Boolean),
    [selectedVendors]
  );

  const toggleVendorType = (vendorType) => {
    setExpandedVendorTypes((prev) => ({
      ...prev,
      [vendorType]: !prev[vendorType],
    }));
  };

  const getCategoryRefKey = (type, disciplineName, categoryName) =>
    `${type}__${disciplineName}__${categoryName}`;

  const scrollToCategory = (type, disciplineName, categoryName) => {
    if (!type || !disciplineName || !categoryName) return;

    const key = getCategoryRefKey(type, disciplineName, categoryName);

    const scrollWhenReady = (attempt = 0) => {
      const node = categoryRefs.current[key];

      if (node) {
        node.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setHighlightedCategoryRefKey(key);
        window.clearTimeout(categoryHighlightTimerRef.current);
        categoryHighlightTimerRef.current = window.setTimeout(() => {
          setHighlightedCategoryRefKey("");
        }, 2200);
        return;
      }

      if (attempt < 8) {
        window.setTimeout(() => scrollWhenReady(attempt + 1), 80);
      }
    };

    window.setTimeout(scrollWhenReady, 80);
  };

  const toggleDiscipline = (vendorType, disciplineId) => {
    const disciplineKey = buildKey(vendorType, disciplineId);
    setSelectedDisciplines((prev) => {
      const next = new Set(prev);
      if (next.has(disciplineKey)) {
        next.delete(disciplineKey);
        setSelectedCategories((categoryPrev) => {
          const categoryNext = new Set(categoryPrev);
          [...categoryNext].forEach((key) => {
            if (key.startsWith(`${disciplineKey}|`)) categoryNext.delete(key);
          });
          return categoryNext;
        });
        setSelectedSubCategories((subCategoryPrev) => {
          const subCategoryNext = new Set(subCategoryPrev);
          [...subCategoryNext].forEach((key) => {
            if (key.startsWith(`${disciplineKey}|`)) subCategoryNext.delete(key);
          });
          return subCategoryNext;
        });
      } else {
        next.add(disciplineKey);
      }
      return next;
    });
  };

  const toggleCategory = (vendorType, disciplineId, category, checked) => {
    const disciplineKey = buildKey(vendorType, disciplineId);
    const categoryId = getId(category?._id);
    const categoryKey = buildKey(vendorType, disciplineId, categoryId);

    setSelectedDisciplines((prev) => new Set(prev).add(disciplineKey));
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      const shouldSelect =
        typeof checked === "boolean" ? checked : !next.has(categoryKey);

      if (!shouldSelect) {
        next.delete(categoryKey);
        setSelectedSubCategories((subCategoryPrev) => {
          const subCategoryNext = new Set(subCategoryPrev);
          [...subCategoryNext].forEach((key) => {
            if (key.startsWith(`${categoryKey}|`)) subCategoryNext.delete(key);
          });
          return subCategoryNext;
        });
        return next;
      }

      next.add(categoryKey);
      setSelectedSubCategories((subCategoryPrev) => {
        const subCategoryNext = new Set(subCategoryPrev);
        category?.subCategories?.forEach((subCategory) => {
          const subCategoryId = getId(subCategory?._id);
          if (subCategoryId) {
            subCategoryNext.add(
              buildKey(vendorType, disciplineId, categoryId, subCategoryId)
            );
          }
        });
        return subCategoryNext;
      });
      return next;
    });
  };

  const toggleSubCategory = (vendorType, disciplineId, categoryId, subCategoryId) => {
    const disciplineKey = buildKey(vendorType, disciplineId);
    const categoryKey = buildKey(vendorType, disciplineId, categoryId);
    const subCategoryKey = buildKey(
      vendorType,
      disciplineId,
      categoryId,
      subCategoryId
    );

    setSelectedDisciplines((prev) => new Set(prev).add(disciplineKey));
    setSelectedCategories((prev) => new Set(prev).add(categoryKey));
    setSelectedSubCategories((prev) => {
      const next = new Set(prev);
      if (next.has(subCategoryKey)) {
        next.delete(subCategoryKey);
      } else {
        next.add(subCategoryKey);
      }
      return next;
    });
  };

  const applyTaxonomySearchResult = (result, checked = true) => {
    const vendorType = result?.vendorType;
    const disciplineId = getId(result?.disciplineId || result?.discipline?._id);
    if (!vendorType || !disciplineId) return;

    const disciplineKey = buildKey(vendorType, disciplineId);

    if (!checked) {
      if (result?.subCategoryName) {
        const categoryId = getId(result?.categoryId || result?.category?._id);
        const subCategoryId = getId(result?.subCategoryId || result?.subCategory?._id);
        const subCategoryKey = buildKey(
          vendorType,
          disciplineId,
          categoryId,
          subCategoryId
        );

        setSelectedSubCategories((prev) => {
          const next = new Set(prev);
          next.delete(subCategoryKey);
          return next;
        });
        return;
      }

      if (result?.categoryName) {
        const categoryId = getId(result?.categoryId || result?.category?._id);
        const categoryKey = buildKey(vendorType, disciplineId, categoryId);

        setSelectedCategories((prev) => {
          const next = new Set(prev);
          next.delete(categoryKey);
          return next;
        });
        setSelectedSubCategories((prev) => {
          const next = new Set(prev);
          [...next].forEach((key) => {
            if (key.startsWith(`${categoryKey}|`)) next.delete(key);
          });
          return next;
        });
        return;
      }

      setSelectedDisciplines((prev) => {
        const next = new Set(prev);
        next.delete(disciplineKey);
        return next;
      });
      setSelectedCategories((prev) => {
        const next = new Set(prev);
        [...next].forEach((key) => {
          if (key.startsWith(`${disciplineKey}|`)) next.delete(key);
        });
        return next;
      });
      setSelectedSubCategories((prev) => {
        const next = new Set(prev);
        [...next].forEach((key) => {
          if (key.startsWith(`${disciplineKey}|`)) next.delete(key);
        });
        return next;
      });
      return;
    }

    setExpandedVendorTypes((prev) => ({ ...prev, [vendorType]: true }));
    setSelectedDisciplines((prev) => new Set(prev).add(disciplineKey));

    if (!result?.categoryName) return;

    const categoryId = getId(result?.categoryId || result?.category?._id);
    if (!categoryId) return;

    const categoryKey = buildKey(vendorType, disciplineId, categoryId);
    setSelectedCategories((prev) => new Set(prev).add(categoryKey));
    scrollToCategory(vendorType, result.disciplineName, result.categoryName);

    if (result?.subCategoryName) {
      const subCategoryId = getId(result?.subCategoryId || result?.subCategory?._id);
      if (!subCategoryId) return;

      setSelectedSubCategories((prev) =>
        new Set(prev).add(
          buildKey(vendorType, disciplineId, categoryId, subCategoryId)
        )
      );
      return;
    }

    setSelectedSubCategories((prev) => {
      const next = new Set(prev);
      result?.category?.subCategories?.forEach((subCategory) => {
        const subCategoryId = getId(subCategory?._id);
        if (subCategoryId) {
          next.add(buildKey(vendorType, disciplineId, categoryId, subCategoryId));
        }
      });
      return next;
    });
  };

  const isTaxonomySearchResultSelected = (result) => {
    const vendorType = result?.vendorType;
    const disciplineId = getId(result?.disciplineId || result?.discipline?._id);
    if (!vendorType || !disciplineId) return false;

    if (result?.subCategoryName) {
      const categoryId = getId(result?.categoryId || result?.category?._id);
      const subCategoryId = getId(result?.subCategoryId || result?.subCategory?._id);
      return selectedSubCategories.has(
        buildKey(vendorType, disciplineId, categoryId, subCategoryId)
      );
    }

    if (result?.categoryName) {
      const categoryId = getId(result?.categoryId || result?.category?._id);
      return selectedCategories.has(buildKey(vendorType, disciplineId, categoryId));
    }

    return selectedDisciplines.has(buildKey(vendorType, disciplineId));
  };

  const buildVendorCategoryHierarchy = () => {
    const output = [];

    Object.entries(taxonomyAll || {}).forEach(([vendorType, taxonomy]) => {
      const typeEntry = { vendorType, disciplines: [] };

      taxonomy?.disciplines?.forEach((discipline) => {
        const disciplineId = getId(discipline?._id);
        const disciplineKey = buildKey(vendorType, disciplineId);
        if (!selectedDisciplines.has(disciplineKey)) return;

        const disciplineEntry = {
          disciplineId,
          categories: [],
        };

        discipline?.categories?.forEach((category) => {
          const categoryId = getId(category?._id);
          const categoryKey = buildKey(vendorType, disciplineId, categoryId);
          if (!selectedCategories.has(categoryKey)) return;

          const categoryEntry = {
            categoryId,
            subCategories: [],
          };

          category?.subCategories?.forEach((subCategory) => {
            const subCategoryId = getId(subCategory?._id);
            const subCategoryKey = buildKey(
              vendorType,
              disciplineId,
              categoryId,
              subCategoryId
            );

            if (selectedSubCategories.has(subCategoryKey)) {
              categoryEntry.subCategories.push({ subCategoryId, ...(Object.prototype.hasOwnProperty.call(makeIdsBySubcategory, subCategoryId) ? { makeIds: makeIdsBySubcategory[subCategoryId] } : {}) });
            }
          });

          disciplineEntry.categories.push(categoryEntry);
        });

        typeEntry.disciplines.push(disciplineEntry);
      });

      if (typeEntry.disciplines.length > 0) output.push(typeEntry);
    });

    return output;
  };

  const handleApply = () => {
    onApply(buildVendorCategoryHierarchy());
  };

  if (isDrawer ? !drawerMounted : !open) return null;

  return (
    <div
      className={`fixed inset-0 z-[10000] flex bg-gray-500 transition-colors duration-300 ${isDrawer ? `justify-end ${open && drawerVisible ? "bg-opacity-50" : "bg-opacity-0"}` : "items-center justify-center bg-opacity-50"}`}
      onClick={isDrawer ? onClose : undefined}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className={isDrawer
          ? `relative flex h-full w-full transform flex-col bg-white shadow-2xl transition-transform duration-300 ease-out md:w-[90vw] ${open && drawerVisible ? "translate-x-0" : "translate-x-full"}`
          : "relative mx-auto w-full max-w-7xl rounded-lg bg-white shadow-md"}
      >
        <div className="flex shrink-0 items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-xl font-bold">{title}</h3>
            <p className="mt-1 text-xs text-gray-500">
              {selectedVendors.length} vendor{selectedVendors.length > 1 ? "s" : ""} selected
              {selectedVendorNames.length > 0
                ? `: ${selectedVendorNames.slice(0, 3).join(", ")}${selectedVendorNames.length > 3 ? "..." : ""}`
                : ""}
            </p>
          </div>
          <button type="button" className="text-sm text-gray-600 hover:text-black" onClick={onClose}>
            Close
          </button>
        </div>

        <div className={`${isDrawer ? "min-h-0 flex-1" : "max-h-[65vh]"} overflow-y-auto bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF] px-5 py-4`}>
          {taxonomyLoading || !taxonomyLoaded ? (
            <div className="flex h-56 flex-col items-center justify-center gap-3">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-orange-100 border-t-orange-500" />
              <p className="text-xs font-medium text-orange-600">
                Loading vendor categories...
              </p>
            </div>
          ) : Object.keys(taxonomyAll || {}).length === 0 ? (
            <p className="text-sm text-gray-500">No vendor taxonomy found.</p>
          ) : (
            <div className="space-y-4">
              <VendorMakeSelections
                selections={materialMakeSelections(buildVendorCategoryHierarchy(), taxonomyAll)}
                onChange={(id, makeIds) => setMakeIdsBySubcategory((current) => ({ ...current, [id]: makeIds }))}
                onRemove={(removed) => setMakeIdsBySubcategory((current) => {
                  const next = { ...current };
                  removed.forEach((id) => { next[id] = []; });
                  return next;
                })}
              >
              {(renderMakes) => <VendorTaxonomySearch
                renderSubCategoryDetails={(type, discipline, category, subCategory, options) => renderMakes(subCategory._id, options)}
                taxonomyAll={taxonomyAll}
                searchTerm={categorySearchTerm}
                onSearchTermChange={setCategorySearchTerm}
                showTree
                highlightedCategoryRefKey={highlightedCategoryRefKey}
                isResultSelected={isTaxonomySearchResultSelected}
                onResultSelect={applyTaxonomySearchResult}
                isVendorTypeSelected={(vendorType) =>
                  expandedVendorTypes[vendorType] ||
                  [...selectedDisciplines].some((key) => key.startsWith(`${vendorType}|`))
                }
                onVendorTypeToggle={(vendorType, checked) => {
                  setExpandedVendorTypes((prev) => ({
                    ...prev,
                    [vendorType]: checked,
                  }));

                  if (checked) return;

                  setSelectedDisciplines((prev) => {
                    const next = new Set(prev);
                    [...next].forEach((key) => {
                      if (key.startsWith(`${vendorType}|`)) next.delete(key);
                    });
                    return next;
                  });
                  setSelectedCategories((prev) => {
                    const next = new Set(prev);
                    [...next].forEach((key) => {
                      if (key.startsWith(`${vendorType}|`)) next.delete(key);
                    });
                    return next;
                  });
                  setSelectedSubCategories((prev) => {
                    const next = new Set(prev);
                    [...next].forEach((key) => {
                      if (key.startsWith(`${vendorType}|`)) next.delete(key);
                    });
                    return next;
                  });
                }}
                isDisciplineSelected={(vendorType, discipline) =>
                  selectedDisciplines.has(buildKey(vendorType, getId(discipline?._id)))
                }
                onDisciplineToggle={(vendorType, discipline) =>
                  toggleDiscipline(vendorType, getId(discipline?._id))
                }
                isCategorySelected={(vendorType, discipline, category) =>
                  selectedCategories.has(
                    buildKey(vendorType, getId(discipline?._id), getId(category?._id))
                  )
                }
                onCategoryToggle={(vendorType, discipline, category, checked) =>
                  toggleCategory(vendorType, getId(discipline?._id), category, checked)
                }
                isSubCategorySelected={(vendorType, discipline, category, subCategory) =>
                  selectedSubCategories.has(
                    buildKey(
                      vendorType,
                      getId(discipline?._id),
                      getId(category?._id),
                      getId(subCategory?._id)
                    )
                  )
                }
                onSubCategoryToggle={(vendorType, discipline, category, subCategory) =>
                  toggleSubCategory(
                    vendorType,
                    getId(discipline?._id),
                    getId(category?._id),
                    getId(subCategory?._id)
                  )
                }
                getCategoryRefKey={getCategoryRefKey}
                onCategoryRef={({ node, refKey }) => {
                  if (node && refKey) {
                    categoryRefs.current[refKey] = node;
                  }
                }}
              />}
              </VendorMakeSelections>

              {false && Object.entries(taxonomyAll).map(([vendorType, taxonomy]) => (
                <div key={vendorType} className="rounded-md border border-gray-200">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between border-b bg-gray-50 px-4 py-2 text-left text-sm font-semibold"
                    onClick={() => toggleVendorType(vendorType)}
                  >
                    <span className="flex items-center gap-2">
                      {vendorType}
                      <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-normal text-gray-500">
                        {taxonomy?.disciplines?.length || 0} disciplines
                      </span>
                    </span>
                    <span className="text-xs text-gray-500">
                      {expandedVendorTypes[vendorType] ? "Collapse" : "Expand"}
                    </span>
                  </button>

                  {expandedVendorTypes[vendorType] && (
                    <div className="space-y-3 p-4">
                    {taxonomy?.disciplines?.map((discipline) => {
                      const disciplineId = getId(discipline?._id);
                      const disciplineKey = buildKey(vendorType, disciplineId);

                      return (
                        <div key={disciplineId} className="rounded border border-gray-100 p-3">
                          <label className="flex items-center gap-2 text-sm font-medium">
                            <input
                              type="checkbox"
                              checked={selectedDisciplines.has(disciplineKey)}
                              onChange={() => toggleDiscipline(vendorType, disciplineId)}
                            />
                            <span>{discipline?.name}</span>
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-normal text-gray-500">
                              {discipline?.categories?.length || 0} categories
                            </span>
                          </label>

                          {selectedDisciplines.has(disciplineKey) && (
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                              {discipline?.categories?.map((category) => {
                                const categoryId = getId(category?._id);
                                const categoryKey = buildKey(
                                  vendorType,
                                  disciplineId,
                                  categoryId
                                );

                                return (
                                  <div key={categoryId} className="rounded bg-gray-50 p-3">
                                    <label className="flex items-center gap-2 text-xs font-semibold">
                                      <input
                                        type="checkbox"
                                        checked={selectedCategories.has(categoryKey)}
                                        onChange={() =>
                                          toggleCategory(vendorType, disciplineId, category)
                                        }
                                      />
                                      <span>{category?.name}</span>
                                      <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-normal text-gray-500">
                                        {category?.subCategories?.length || 0} subcategories
                                      </span>
                                    </label>

                                    {selectedCategories.has(categoryKey) &&
                                      category?.subCategories?.length > 0 && (
                                        <div className="mt-2 grid gap-2">
                                          {category.subCategories.map((subCategory) => {
                                            const subCategoryId = getId(subCategory?._id);
                                            const subCategoryKey = buildKey(
                                              vendorType,
                                              disciplineId,
                                              categoryId,
                                              subCategoryId
                                            );

                                            return (
                                              <label
                                                key={subCategoryId}
                                                className="flex items-center gap-2 text-xs text-gray-700"
                                              >
                                                <input
                                                  type="checkbox"
                                                  checked={selectedSubCategories.has(subCategoryKey)}
                                                  onChange={() =>
                                                    toggleSubCategory(
                                                      vendorType,
                                                      disciplineId,
                                                      categoryId,
                                                      subCategoryId
                                                    )
                                                  }
                                                />
                                                {subCategory?.name}
                                              </label>
                                            );
                                          })}
                                        </div>
                                      )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 px-5 py-4">
          {showError?.status ? (
            <p className="text-xs text-red-700">{showError?.message}</p>
          ) : (
            <p className="text-xs text-gray-500">
              {footerNote}
            </p>
          )}
          <button
            disabled={!hasSelection || loading || taxonomyLoading}
            onClick={handleApply}
            className={`rounded-md px-4 py-2 text-xs font-medium ${
              hasSelection && !loading && !taxonomyLoading
                ? "bg-black text-white hover:shadow-md"
                : "cursor-not-allowed bg-gray-300 text-gray-100"
            }`}
          >
            {loading ? <Loading /> : applyButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCategoryPopUp;

